import { useEffect, useState } from 'react';
import { Link2, MessageSquareText, PlusCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { copiarAlPortapapeles } from '@renderer/lib/portapapeles';
import { mensajeMandarMenu } from '@shared/mensajesWhatsapp';
import { PLANTILLA_CANONICA_ES_MX } from '@shared/parsePedidoWa';
import { esWeb } from '@renderer/lib/entorno';
import { TarjetaConReverso } from './TarjetaConReverso';
import {
  CLAVE_TARJETAS_OCULTAS,
  tarjetasVisibles,
  type TarjetaDomicilio,
} from '@shared/avisosDomicilio';

/**
 * Sprint 95 — Por dónde empieza un pedido a domicilio.
 *
 * ## El hueco que tapa
 *
 * El dueño del producto describió sus dos flujos y los mapeamos contra el sistema: de nueve pasos,
 * ocho estaban. **Los dos que faltaban eran los primeros de cada uno.**
 *
 * - *"El cliente pide el menú → **se le envía**"* — no vivía en ningún lado operativo. El enlace
 *   estaba en Configuración, que es donde se **configura**, no donde se **usa** 150 veces al día.
 * - *"Se recepciona e interpreta el pedido"* — existía, pero enterrado en el paso 3 de 5 del
 *   asistente del POS. Para leer el mensaje del cliente había que capturar antes **quién** es y
 *   **dónde** vive: el sistema pidiendo lo que ya tenía enfrente, el mismo error del Sprint 76.
 *
 * La causa de fondo era estructural: el tablero modela **pedidos vivos**, y en el primer paso
 * todavía no hay pedido. Por eso no cabía en ninguna columna y por eso hace falta esto — una
 * cabecera que no habla de pedidos, sino de **cómo empieza uno**.
 *
 * ## Por qué son tarjetas grandes
 *
 * Porque el acto de descubrimiento tiene que ser evidente. Un botón discreto en una barra no lo usa
 * nadie: si el atajo no se ve desde dos metros, se sigue tecleando a mano. Y cada una puede
 * **voltearse** para explicar los pasos que ocurren fuera del sistema, que es justo donde la gente
 * se atora.
 */
export function ComoEmpiezaElPedido() {
  const navigate = useNavigate();
  const negocio = useNegocioStore((s) => s.negocio);
  const cargarNegocio = useNegocioStore((s) => s.cargar);

  const [copiado, setCopiado] = useState(false);
  /**
   * Sprint 108 — El negocio decide qué tarjetas ve.
   *
   * *"Sería genial poder apagar los cards (configurable si ya no tiene uso)"*. Se apaga la
   * tarjeta, no el camino: lo que se oculta es el atajo de la portada.
   */
  const [visibles, setVisibles] = useState<TarjetaDomicilio[]>(() => tarjetasVisibles(null));

  useEffect(() => {
    void (async () => {
      const r = await window.api.config.obtener(CLAVE_TARJETAS_OCULTAS);
      setVisibles(tarjetasVisibles(r.ok ? r.data : null));
    })();
  }, []);
  const [copiadaPlantilla, setCopiadaPlantilla] = useState(false);

  /**
   * Sprint 98 — La plantilla que hace que el cliente escriba como el intérprete ya entiende.
   *
   * **No es una plantilla nueva**: es la misma que existe desde el Sprint 35 y que se edita en
   * Configuración. Lo que faltaba era tenerla **donde se usa**. Configuración es donde se
   * configura; esto es donde se trabaja 150 veces al día — el mismo error que el Sprint 95
   * corrigió con el enlace del menú.
   *
   * Si la dueña la editó, se copia la suya; si no, la canónica del código.
   */
  const copiarPlantilla = async () => {
    const ok = await copiarAlPortapapeles(
      negocio?.plantillaPedidoWa?.trim() || PLANTILLA_CANONICA_ES_MX,
    );
    setCopiadaPlantilla(ok);
  };

  const menuEncendido = negocio?.menuPublicoActivo === true;
  const enlace = esWeb() ? `${window.location.origin}/menu` : null;

  /**
   * Apagar el enlace en un toque.
   *
   * Es la contraparte del enlace abierto: no se puede hacer a prueba de trolls, pero sí se puede
   * hacer que cerrarlo cueste un segundo. Con confirmación, porque apagarlo por accidente en hora
   * pico deja al negocio sin su canal nuevo sin que nadie entienda por qué.
   */
  const apagarMenu = async () => {
    const ok = await window.confirmar({
      titulo: 'Apagar el menú en línea',
      mensaje:
        'Tu enlace deja de abrir hasta que lo vuelvas a encender. Los pedidos que ya llegaron no se pierden.',
      textoConfirmar: 'Sí, apagarlo',
      textoCancelar: 'No',
      variant: 'warn',
    });
    if (!ok) return;
    const r = await window.api.negocio.actualizar({ menuPublicoActivo: false });
    if (r.ok) await cargarNegocio();
  };

  const mandarMenu = async () => {
    if (!enlace) return;
    const ok = await copiarAlPortapapeles(
      mensajeMandarMenu({ enlace, nombreNegocio: negocio?.nombre }),
    );
    setCopiado(ok);
  };

  return (
    <>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {/* ── 1. Le mando el menú ── */}
        {visibles.includes('menu') && (
        <div data-uso="domicilio:menu" className="contents">
        <TarjetaConReverso
          titulo="Mándale el menú"
          descripcion={
            menuEncendido
              ? 'Para el cliente que pregunta “¿qué tienes?”. Él arma su pedido solo.'
              : 'Está apagado. Enciéndelo en Configuración para poder usarlo.'
          }
          icono={<Link2 className="h-6 w-6" />}
          acento={{
            borde: 'border-blue-300',
            fondo: 'bg-blue-50',
            texto: 'text-blue-900',
            boton: 'bg-blue-600 hover:bg-blue-700',
          }}
          onAccion={() => {
            if (!menuEncendido || !enlace) navigate('/settings?tab=whatsapp');
            else void mandarMenu();
          }}
          /*
            Sprint 96 — El apagador, a la mano.
            Si te están troleando con pedidos falsos, apagar el enlace tiene que ser un toque desde
            aquí y no cinco pantallas adentro de Configuración. Una defensa que tarda no es defensa.
          */
          alPie={
            menuEncendido
              ? {
                  texto: 'Apagar el menú en línea',
                  onClick: apagarMenu,
                }
              : undefined
          }
          textoAccion={
            !menuEncendido || !enlace ? 'Encenderlo en Configuración' : copiado ? '✓ Copiado' : 'Copiar el mensaje'
          }
          pie={
            copiado && menuEncendido ? (
              <span className="font-semibold text-blue-800">Pégalo en el chat del cliente</span>
            ) : null
          }
          comoSeUsa={[
            'El cliente te escribe preguntando qué tienes.',
            'Aprieta “Copiar el mensaje”: se copia con tu enlace incluido.',
            'Pégalo en su chat de WhatsApp y mándalo.',
            'Él elige sus productos, pone su nombre y comparte su ubicación.',
            'Su pedido aparece aquí abajo, en “Llegaron del enlace”.',
            'Tú lo revisas, cotizas el envío y le pasas el total.',
          ]}
        />
        </div>
        )}

        {/* ── 2. Me escribió por WhatsApp ── */}
        {visibles.includes('chat') && (
        <div data-uso="domicilio:chat" className="contents">
        <TarjetaConReverso
          titulo="Te lo pidió por WhatsApp"
          descripcion="Pega su mensaje y lo convierto en pedido. Lo que no entienda, te lo pregunto."
          icono={<MessageSquareText className="h-6 w-6" />}
          acento={{
            borde: 'border-violet-300',
            fondo: 'bg-violet-50',
            texto: 'text-violet-900',
            boton: 'bg-violet-600 hover:bg-violet-700',
          }}
          /*
            Sprint 98 — Esta tarjeta ya no abre un modal propio: abre el pedido.

            Abría "¿Qué te pidió?" aquí, y al terminar aparecía "Datos del cliente" con **otro**
            botón para pegar el mensaje. Dos puertas para el mismo gesto, en fila. Ahora entra
            directo a la captura, donde pegar el chat es lo primero que se ofrece y de un solo
            pegado sale todo: el pedido y quién lo pidió.
          */
          onAccion={() => navigate('/pos?tipo=delivery&origen=domicilios&canal=chat')}
          textoAccion="Pegar lo que me escribió"
          /*
            Sprint 98 — Enseñarle al cliente a escribir, en vez de enseñarle al programa a adivinar.

            Con un mensaje ordenado —nombre, calle, celular y la lista— el sistema saca **todo** de
            un solo pegado y la ventana siguiente sólo se confirma. Esta plantilla es lo que hace
            que eso pase seguido: se copia y se manda al chat del cliente.
          */
          alPie={{
            texto: copiadaPlantilla ? '✓ Plantilla copiada' : 'Copiar la plantilla para el cliente',
            onClick: copiarPlantilla,
          }}
          comoSeUsa={[
            'Copia el mensaje del cliente en WhatsApp (mantén apretado → Copiar).',
            'Aprieta “Pegar lo que me escribió”: se abre la captura del pedido.',
            'Ahí mismo aprieta el recuadro verde y pega el mensaje.',
            'Reviso lo que entendí —productos, nombre y celular— y te pregunto lo que no.',
            'Corriges lo que haga falta y sigues con su dirección y el costo del envío.',
            'Si algo no está en tu menú, búscalo y elígelo: me lo aprendo para la próxima.',
          ]}
        />
        </div>
        )}

        {/* ── 3. Me lo dicta por teléfono ── */}
        {visibles.includes('telefono') && (
        <div data-uso="domicilio:telefono" className="contents">
        <TarjetaConReverso
          /*
            Sprint 108 — Se llamaba "Lo capturo yo", que no decía **a domicilio** por ningún lado
            estando dentro de esta sección. El propio dueño del producto la confundió con la venta
            de mostrador, que es otra cosa: aquí hay dirección, moto y envío que cotizar.
          */
          titulo="Me lo dicta por teléfono"
          descripcion="El de siempre: llama, te dicta y tú vas anotando."
          icono={<PlusCircle className="h-6 w-6" />}
          acento={{
            borde: 'border-slate-300',
            fondo: 'bg-slate-50',
            texto: 'text-slate-800',
            boton: 'bg-slate-700 hover:bg-slate-800',
          }}
          onAccion={() => navigate('/pos?tipo=delivery&origen=domicilios&canal=telefono')}
          textoAccion="Nuevo pedido a domicilio"
          comoSeUsa={[
            'Aprieta “Nuevo pedido a domicilio”.',
            'Capturas quién es y a dónde va.',
            'Eliges sus productos del menú.',
            'Cotizas el envío con el grupo de motomandados.',
            'Le pasas el total y esperas su transferencia.',
          ]}
        />
        </div>
        )}
      </div>

    </>
  );
}
